import React from 'react'
import { Button } from '../Button/Button.jsx'

/**
 * Collapsible inline filter panel. Expands/collapses via a CSS
 * max-height + opacity transition (no JS measurement). Host owns the
 * `open` boolean; the panel just reflects it.
 *
 * Compound API:
 *   <FilterPanel open={open} onClearAll={fn} clearDisabled={!hasActive} id="tasks-filters">
 *     <FilterPanel.Group label="Status">{chips}</FilterPanel.Group>
 *     …
 *   </FilterPanel>
 *
 * See design-language.md §6.9.
 */
export function FilterPanel({
  open = false,
  onClearAll,
  clearDisabled = false,
  id,
  ariaLabel = 'Filters',
  className = '',
  children,
  ...rest
}) {
  const classes = ['filter-panel', className].filter(Boolean).join(' ')
  // No `aria-hidden` — the button that controls the panel carries
  // `aria-expanded` + `aria-controls`, which is the correct disclosure
  // semantic. Visibility is handled by CSS so focusable descendants
  // leave the tab order when collapsed.
  return (
    <div
      className={classes}
      data-open={open ? 'true' : 'false'}
      id={id}
      role="region"
      aria-label={ariaLabel}
      {...rest}
    >
      <div className="filter-panel__inner">
        <div className="filter-panel__row">{children}</div>
        <div className="filter-panel__footer">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            disabled={clearDisabled}
          >
            Clear all
          </Button>
        </div>
      </div>
    </div>
  )
}

function FilterGroup({ label, className = '', children, ...rest }) {
  const classes = ['filter-group', className].filter(Boolean).join(' ')
  return (
    <div className={classes} role="group" aria-label={label} {...rest}>
      <div className="filter-group__label">{label}</div>
      <div className="filter-group__chips">{children}</div>
    </div>
  )
}

FilterPanel.Group = FilterGroup
